import React from 'react';
import { Box, IconButton, Typography, SxProps, Stack } from '@mui/material';
import { CloseIcon } from '../Icons';
import ImageRender from '../ImageRender/ImageRender';
import CustomSwiper from '../Swiper/CustomSwiper';
import { Product } from '@src/interfaces/type';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import styled from 'styled-components';
import { IsMobileWidth } from '../MediaQueryUtils';
import { useAppSelector } from '@src/store/hooks';
import { RESTAURANT } from '@src/Constants';

const FieldConatiner = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	'#key': {
		fontSize: '14px',
		fontWeight: 400,
		color: '#979797',
	},
	'#value-container': {
		display: 'flex',
		alignItems: 'center',
		paddingLeft: '4px',
		minWidth: 'auto',
		maxWidth: '150px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		fontSize: '14px',
		fontWeight: 600,
		color: '#7D7D7D',
	},
});
interface FieldProp {
	fieldKey: string;
	value: string;
	valueStyle?: any;
	keyStyle?: any;
}

const Field: React.FC<FieldProp> = ({ fieldKey, value, valueStyle = {}, keyStyle = {} }) => {
	if (value == 'N/A') return null;
	return (
		<FieldConatiner>
			{!!fieldKey ? (
				<div id="key" style={keyStyle}>
					{fieldKey}
				</div>
			) : null}
			<CustomTooltip title={value} placement="top">
				<div id="value-container" style={valueStyle}>
					{value}
				</div>
			</CustomTooltip>
		</FieldConatiner>
	);
};

interface ProductDetailsProps {
	setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	data: Product;
}
const headingStyle = {
	color: '#1A1A1A',
	fontSize: '14px',
	fontWeight: 700,
};

const ProductDetails: React.FC<ProductDetailsProps> = ({ setOpenModal, data }) => {
	const isMobile = IsMobileWidth();
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;
	return (
		<Box
			sx={{
				minWidth: '595px',
				height: '100%',
				'@media (max-width: 600px)': {
					minWidth: '100%',
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: '1.5px solid #F1F1F1',
					height: '56px',
					pl: '16px',
					pr: '6px',
				}}
			>
				<Typography variant="body1">{data?.name}</Typography>
				<IconButton onClick={() => setOpenModal(false)}>
					<CloseIcon />
				</IconButton>
			</Box>
			<Box
				sx={{
					px: '16px',
					py: '14px',
					overflowY: 'scroll',
					overflowX: 'hidden',
					height: '380px',
					mb: '8px',
					'@media (max-width: 600px)': {
						height: '570px',
					},
				}}
			>
				<Box
					sx={{
						height: isMobile ? '150px' : '235px',
						width: '100%',
						backgroundColor: '#FFFDF5',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						overflow: 'hidden',
					}}
				>
					{Array.isArray(data?.images) && data?.images?.length ? (
						//@ts-ignore
						<CustomSwiper images={data?.images} />
					) : (
						<ImageRender
							imageUrl={data?.image}
							imageStyles={{ width: '150px', height: '150px', objectFit: 'contain' }}
							fallBackUrl="/placeholderImage.svg"
						/>
					)}
				</Box>
				{data?.description ? (
					<Box sx={{ py: '14px' }}>
						<Typography style={headingStyle} sx={{ pb: 1 }}>
							Product Description
						</Typography>
						<Typography variant="body2">
							{data?.description === '' ? 'Not found' : data?.description}
						</Typography>
					</Box>
				) : null}
				<Box>
					<Typography style={headingStyle} sx={{ pb: 1 }}>
						Product Details
					</Typography>
					<Stack direction={isMobile ? 'column' : 'row'}>
						<div style={{ width: '300px' }}>
							{/* <Field fieldKey="SKU" value={data?.sku || 'N/A'} /> */}
							<Field fieldKey="Part#" value={data?.partNumber || 'N/A'} />
							<Field
								fieldKey="Price:"
								value={
									isRestaurant
										? `Rs. ${data.customerPrice}`
										: data?.price
										? `Rs. ${data.price}`
										: 'N/A'
								}
							/>
							<Field fieldKey="Brand:" value={data?.brand || 'N/A'} />
							<Field fieldKey="Machine Brand" value={data?.machineBrand || 'N/A'} />
							<Field fieldKey="Machine Model:" value={data?.machineModel || 'N/A'} />
							<Field fieldKey="Group Category:" value={data?.groupCategory || 'N/A'} />
							<Field fieldKey="Category:" value={data?.category || 'N/A'} />
							<Field fieldKey="Sub-Cat:" value={data?.subCategory || 'N/A'} />
						</div>
						<div style={{ width: '300px' }}>
							<Field fieldKey="Composition:" value={data?.partComposition || 'N/A'} />
							<Field fieldKey="Design:" value={data?.partDesign || 'N/A'} />
							<Field fieldKey="Diameter-id:" value={data?.partDiameter || 'N/A'} />
							<Field fieldKey="Dimension-id" value={data?.partDimension || 'N/A'} />
							<Field fieldKey="Ownership-id" value={data?.partOwnership || 'N/A'} />
							<Field fieldKey="Performance-id" value={data?.partPerformance || 'N/A'} />
							<Field fieldKey="Quality-id" value={data?.partQuality || 'N/A'} />
							<Field fieldKey="Weight-id" value={data?.partWeight || 'N/A'} />
						</div>
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};

export default ProductDetails;
